import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// @mui material components
import { Tab, Divider, Tabs, Autocomplete, TextField } from "@mui/material";
import PropTypes from "prop-types";

// React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

// layouts components
import DateToDate from "layouts/transactions/components/DateToDate";
import FormInput from "layouts/transactions/components/FormInput";
import CompliteCurrently from "layouts/transactions/components/CompliteCurrently";
import CompliteLimit from "layouts/transactions/components/CompliteLimit";

import ConfiguratorRoot from "examples/FilterConfigurator/ConfiguratorRoot";
import CloseBtn from "components/CloseBtn";
import CompliteCommand from "layouts/transactions/components/CompliteCommand";
import { timezoneOptions } from "utils/timeZoneData";

const FilterConfigurator = (props) => {
  const { t } = useTranslation();
  const {
    handleCloseMenu,
    DataCSV,
    openMenu,
    setQuery,
    setFilter,
    setLimit,
    limit,
    downloadLoading,
    filter,
    page,
    setPage,
  } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const [timezone, setTimezone] = React.useState("UTC");

  const statusTabs = [
    { key: "All", label: t("status.all") },
    { key: "Completed", label: t("status.completed") },
    { key: "NmtCheck", label: t("status.nmtcheck") },
    { key: "Failed", label: t("status.failed") },
  ];
  const [status, setStatus] = React.useState(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const foundIndex = statusTabs.findIndex(
        (tab) => tab.key.toLowerCase() === statusParam.toLowerCase()
      );
      return foundIndex !== -1 ? foundIndex : 0;
    }
    return 0;
  });

  const [command, setCommand] = React.useState(searchParams.get("command") || "");
  const [req_acc, setReqAcc] = React.useState(searchParams.get("req_acc") || "");
  const [req_id, setReqId] = React.useState(searchParams.get("req_id") || "");
  const [fio, setFio] = React.useState(searchParams.get("fio") || "");
  const [minAmount, setMinAmount] = React.useState(searchParams.get("minAmount") || "");
  const [maxAmount, setMaxAmount] = React.useState(searchParams.get("maxAmount") || "");
  const [currency, setCurrency] = React.useState(searchParams.get("currency") || "");
  const [dateFrom, setDateFrom] = React.useState(searchParams.get("dateFrom") || "");
  const [dateTo, setDateTo] = React.useState(searchParams.get("dateTo") || "");

  useEffect(() => {
    const timezoneFromParams = searchParams.get("timezone");

    if (timezoneFromParams) {
      setTimezone(timezoneFromParams);
    } else {
      const saved = localStorage.getItem("timezone");
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (saved) {
        setTimezone(saved);
      } else {
        setTimezone(userTimezone);
        localStorage.setItem("timezone", userTimezone);
      }
    }
  }, [searchParams]);
  const handleTimezoneChange = (newTimezone) => {
    setTimezone(newTimezone);
  };

  return (
    <ConfiguratorRoot variant="permanent" position="relative" zIndex={1000} ownerState={{ openConfigurator: openMenu }}>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
        zIndex={1000}
      >
        <SoftBox>
          <SoftTypography variant="h5">{t("filters.title")}</SoftTypography>
        </SoftBox>
        <CloseBtn handleCloseMenu={handleCloseMenu} />
      </SoftBox>
      <Divider />
      <SoftBox component="form" pt={1.25} pb={3} px={1}>
        <SoftBox>
          <SoftBox mb={0.5} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              {t("filters.status")}
            </SoftTypography>
          </SoftBox>
          <Tabs
            value={status}
            onChange={(___, newValue) => setStatus(newValue)}
            TabIndicatorProps={{
              style: {
                backgroundImage: "linear-gradient(310deg, #f53939, #fbcf33)",
              },
            }}
          >
            {statusTabs.map((tab, index) => (
              <Tab key={index} label={tab.label === "Pending" ? "NmtCheck" : tab.label} disableRipple />
            ))}
          </Tabs>
        </SoftBox>
        <SoftBox display="flex" alignItems="center" gap={2}>
          <SoftBox mb={0.5}>
            <SoftBox mb={0.5} ml={0.5}>
              <SoftTypography component="label" variant="caption" fontWeight="bold">
                {t("filters.command")}
              </SoftTypography>
            </SoftBox>
            <CompliteCommand
              value={command}
              onChangeCompliteValue={(___, newValue) => setCommand(newValue)}
            />
          </SoftBox>
        </SoftBox>
        <FormInput
          name={"req_acc"}
          placeholder={t("filter.req_acc_placeholder")}
          value={req_acc}
          onChange={(e) => setReqAcc(e.target.value)}
        />
        <FormInput
          name={"req_id / pay_uno"}
          placeholder={t("filter.req_id_placeholder")}
          value={req_id}
          onChange={(e) => setReqId(e.target.value)}
        />
        {/* <FormInput
          name={"fio"}
          placeholder={"fio"}
          value={fio}
          onChange={(e) => setFio(e.target.value)}
        /> */}
        <SoftBox mb={0.5}>
          <SoftBox mb={0.5} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              {t("filters.amount_range")}
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftInput
              type="number"
              placeholder={t("filter.min_placeholder")}
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
            />
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              -
            </SoftTypography>
            <SoftInput
              type="number"
              placeholder={t("filter.max_placeholder")}
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
            />
            <CompliteCurrently
              value={currency}
              onChangeCompliteValue={(___, newValue) => setCurrency(newValue)}
            />
          </SoftBox>
        </SoftBox>
        <SoftBox mb={0.5}>
          <SoftBox mb={0.5} ml={0.5}>
            <SoftTypography component="label" variant="caption" fontWeight="bold">
              {t("filters.timezone")}
            </SoftTypography>
          </SoftBox>
          <SoftBox display="flex" alignItems="center" gap={1}>
            <Autocomplete
              value={timezoneOptions.find((opt) => opt.value === timezone) || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleTimezoneChange(newValue.value);
                }
              }}
              options={timezoneOptions}
              getOptionLabel={(option) => option.label}
              disableClearable
              sx={{ width: 250 }}
              renderInput={(params) => (
                <TextField {...params} placeholder={t("filter.timezone_placeholder")} />
              )}
            />
          </SoftBox>
        </SoftBox>
        <DateToDate
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          dateTo={dateTo}
          setDateTo={setDateTo}
        />
        <Divider />
        <CompliteLimit
          value={limit}
          onChangeCompliteValue={(___, newValue) => setLimit(newValue)}
        />
        <SoftBox display="flex" gap={1} justifyContent="space-between" py={3}>
          <SoftButton
            style={{
              width: "100%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            variant="gradient"
            color="warning"
            onClick={() => {
              setQuery({
                queryMeta: {
                  paginate: true,
                  limit: limit,
                  page: 1,
                },
                filterMeta: {
                  // ...pick(values, ["req_acc", "req_id", "type", "command", "currency", "fio"]),
                  ...(req_acc && { req_acc }),
                  ...(req_id && { req_id }),
                  // ...(type && { type }),
                  ...(command && { command }),
                  ...(currency && { currency }),
                  ...(fio && { fio }),
                  ...(statusTabs[status].key !== "All" && {
                    status: statusTabs[status].key.toLowerCase(),
                  }),
                  ...((minAmount || maxAmount) && {
                    amount: {
                      ...(minAmount && { gte: minAmount }),
                      ...(maxAmount && { lte: maxAmount }),
                    },
                  }),
                  ...((dateFrom || dateTo) && {
                    createdAt: {
                      ...(dateFrom && {
                        gte: new Date(dateFrom),
                      }),
                      ...(dateTo && {
                        lte: new Date(dateTo),
                      }),
                    },
                  }),
                },
              });
              setFilter((prev) => !prev);
              setPage(1);
              setSearchParams({
                paginate: true,
                limit: limit,
                page: 1,
                // ...pick(values, ["req_acc", "req_id", "type", "command", "currency", "fio"]),
                ...(req_acc && { req_acc }),
                ...(req_id && { req_id }),
                // ...(type && { type }),
                ...(command && { command }),
                ...(currency && { currency }),
                ...(fio && { fio }),
                ...(statusTabs[status].key !== "All" && {
                  status: statusTabs[status].key.toLowerCase(),
                }),
                ...(minAmount && { minAmount }),
                ...(maxAmount && { maxAmount }),
                ...(dateFrom && { dateFrom }),
                ...(dateTo && { dateTo }),
                ...(timezone && { timezone }),
              });
            }}
          >
            {t("filters.apply").length > 15
              ? `${t("filters.apply").slice(0, 15)}...`
              : t("filters.apply")}
          </SoftButton>

          <SoftButton
            style={{
              width: "100%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
            variant="outlined"
            color="error"
            onClick={() => {
              setQuery({
                queryMeta: {
                  paginate: true,
                  limit: limit,
                  page: 1,
                },
              });
              setFilter((prev) => !prev);
              setStatus(0);
              // setType("");
              setCommand("");
              setReqAcc("");
              setReqId("");
              setFio("");
              setMaxAmount("");
              setMinAmount("");
              setCurrency("");
              setDateFrom("");
              setDateTo("");
              const saved = localStorage.getItem("timezone");
              const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              setTimezone(saved || userTimezone);
              setPage(1);
              setSearchParams({
                paginate: true,
                limit: limit,
                page: 1,
              });
            }}
          >
            {t("filters.reset").length > 15
              ? `${t("filters.reset").slice(0, 15)}...`
              : t("filters.reset")}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </ConfiguratorRoot>
  );
};
FilterConfigurator.defaultProps = {
  handleCloseMenu: () => {},
  handleOpenMenu: () => {},
};

// Typechecking props for the FilterConfigurator
FilterConfigurator.propTypes = {
  handleCloseMenu: PropTypes.any,
  handleOpenMenu: PropTypes.any,
  downloadLoading: PropTypes.bool,
  openMenu: PropTypes.bool,
  setQuery: PropTypes.any,
  page: PropTypes.any,
  setFilter: PropTypes.any,
  setPage: PropTypes.any,
  DataCSV: PropTypes.any,
  setLimit: PropTypes.any,
  limit: PropTypes.any,
  filter: PropTypes.bool,
};

export default FilterConfigurator;
